/**
 * Lookup Service
 * Fetches the AXI code lists that give the descriptions for the codes on the
 * offer and benchmarking records. The lists are small, static and the same for
 * every supplier, so they are fetched once per session.
 */

import axios from 'axios';

/**
 * Shape returned by the webservices: an array of records holding one code
 * column and one description column, sorted by code.
 * The column names differ per list, hence the field names per lookup below.
 */
const LOOKUPS = {
  voedertypes: { path: '/drvotyp', code: 'DEVOTCE', label: 'DEVOTOM' },
  genetica: { path: '/drgenet', code: 'DEGENCE', label: 'DEGENOM' },
  castratie: { path: '/drcastr', code: 'DECASCE', label: 'DECASOM' },
};

/**
 * Fetches one lookup list and normalises it to {code, label} pairs, so callers
 * do not have to know the AXI column names.
 *
 * @param {string} name - one of 'voedertypes', 'genetica', 'castratie'
 * @returns {Promise<Array<{code: string, label: string}>>} Empty when there are none
 * @throws {Error} Throws Error('FORBIDDEN') on 403, rethrows other errors as-is
 */
export const fetchLookup = async name => {
  const lookup = LOOKUPS[name];

  if (!lookup) {
    throw new Error(`Unknown lookup: ${name}`);
  }

  try {
    const response = await axios.get(`${lookup.path}?_format=json`);

    if (!Array.isArray(response.data)) {
      return [];
    }

    return response.data.map(record => ({
      code: record[lookup.code],
      label: record[lookup.label],
    }));
  } catch (err) {
    if (err.response && err.response.status === 403) {
      throw new Error('FORBIDDEN');
    }
    throw err;
  }
};

export const fetchVoedertypes = () => fetchLookup('voedertypes');
export const fetchGenetica = () => fetchLookup('genetica');
export const fetchCastratie = () => fetchLookup('castratie');

/**
 * Fetches all three lists in parallel. They are independent of each other.
 *
 * @returns {Promise<{voedertypes: Array, genetica: Array, castratie: Array}>}
 * @throws Rejects if any of the three fails
 */
export const fetchAllLookups = async () => {
  const [voedertypes, genetica, castratie] = await Promise.all([
    fetchVoedertypes(),
    fetchGenetica(),
    fetchCastratie(),
  ]);

  return { voedertypes, genetica, castratie };
};
